'use client';

import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { isMarketingPublicMode } from '@/lib/publicPortfolioAccess';
import { SESSION_EXPIRED_EVENT } from '@/lib/client/session-expiry';
import { getLogoutDestination } from '@/lib/auth-return-path';

interface User {
  id: string;
  email: string;
  name?: string;
  email_verified?: boolean;
  setup_completed?: boolean;
  portfolio_handle?: string | null;
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  loading: boolean;
  login: () => void; // Keep for backward compatibility (demo mode)
  logout: (returnTo?: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  toggleAuth: () => void; // Keep for backward compatibility (demo mode)
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const DEMO_AUTH_STORAGE_KEY = 'skillbridger.demoAuth';

const getDemoUser = (): User => ({
  id: 'demo-user',
  name: 'Demo User',
  email: 'demo@skillbridger.com',
  email_verified: true,
  setup_completed: false
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const refreshUser = useCallback(async () => {
    if (isMarketingPublicMode()) {
      setUser(null);
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }

    try {
      const isDemoAuth = localStorage.getItem(DEMO_AUTH_STORAGE_KEY) === 'true';
      if (isDemoAuth) {
        setUser(getDemoUser());
        setIsLoggedIn(true);
        return;
      }

      // Get user from OAuth session (NextAuth.js)
      const response = await fetch('/api/auth/session', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const data = await response.json();

      if (response.status === 401 && data.code === 'SESSION_EXPIRED') {
        setUser(null);
        setIsLoggedIn(false);
        setSessionExpired(true);
        return;
      }

      if (data.user) {
        localStorage.removeItem(DEMO_AUTH_STORAGE_KEY);
        // User authenticated via OAuth (LinkedIn/Google)
        const resolvedId = data.user.id || data.user.email?.toLowerCase() || data.user.sub;
        const email = data.user.email?.toLowerCase() || data.user.sub || data.user.id;

        setUser({
          id: resolvedId || email,
          email: data.user.email,
          name: data.user.name || `${data.user.given_name || ''} ${data.user.family_name || ''}`.trim(),
          email_verified: true,
          setup_completed: data.user.setup_completed === true,
          portfolio_handle: data.user.portfolio_handle ?? null
        });
        setIsLoggedIn(true);
        setSessionExpired(false);
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
    } catch {
      setUser(null);
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    void refreshUser();

    const onSessionExpired = () => {
      setUser(null);
      setIsLoggedIn(false);
      setSessionExpired(true);
    };
    const onFocus = () => void refreshUser();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshUser();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const intervalId = window.setInterval(() => void refreshUser(), 10 * 60 * 1000);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [refreshUser]);

  // Backward compatibility: Mock login for development
  const login = () => {
    localStorage.setItem(DEMO_AUTH_STORAGE_KEY, 'true');
    setIsLoggedIn(true);
    setUser(getDemoUser());
    setLoading(false);
  };

  const logout = async (returnTo?: string) => {
    const destination = getLogoutDestination(returnTo);
    try {
      // Sign out via NextAuth.js
      const response = await fetch('/api/auth/signout', {
        method: 'POST',
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Signout request failed');
      localStorage.removeItem(DEMO_AUTH_STORAGE_KEY);
      setIsLoggedIn(false);
      setUser(null);

      // Redirect to signed-out page
      window.location.href = destination;
    } catch (error) {
      console.error('Error signing out:', error);
      if (returnTo) throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      const response = await fetch('/api/user/delete', {
        method: 'DELETE',
        credentials: 'same-origin'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      await fetch('/api/auth/signout', {
        method: 'POST',
        credentials: 'same-origin',
      });

      localStorage.removeItem(DEMO_AUTH_STORAGE_KEY);
      setIsLoggedIn(false);
      setUser(null);
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  };

  const toggleAuth = () => {
    if (isLoggedIn) {
      logout();
    } else {
      login();
    }
  };

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      user,
      loading,
      login,
      logout,
      deleteAccount,
      toggleAuth,
      refreshUser
    }}>
      {children}
      {sessionExpired ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 px-5 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="session-expired-title"
        >
          <div className="w-full max-w-md rounded-3xl border border-orange-200 bg-white p-7 shadow-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">
              Sign-in required
            </p>
            <h2 id="session-expired-title" className="mt-3 text-2xl font-semibold text-slate-950">
              Your session expired
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Your progress is saved. If a generation is already running, it will continue in the
              background.
            </p>
            <a
              className="mt-6 flex min-h-12 w-full items-center justify-center rounded-xl bg-orange-600 px-5 py-3 font-semibold text-white hover:bg-orange-700"
              href={`/auth/login?next=${encodeURIComponent(
                typeof window === 'undefined'
                  ? '/catalog'
                  : `${window.location.pathname}${window.location.search}`
              )}`}
            >
              Sign in to continue
            </a>
          </div>
        </div>
      ) : null}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
